import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './Icon';

/**
 * قائمة منسدلة مرساة إلى زرّها.
 *
 * ثلاثة قيود فرضت هذا الشكل بدل قائمة عادية داخل التدفّق:
 *
 *   ١. موضعها الغالب صفُّ جدول، و`.table-wrap` عليه `overflow-x: auto`
 *      فيقصّ أي شيء يخرج عن حدوده. القائمة تُرسَم في `document.body` عبر
 *      بوّابة، فلا يقصّها أحد.
 *
 *   ٢. الاتجاه من اليمين إلى اليسار: المحاذاة تُحسب من الحافة اليمنى للزرّ
 *      لا اليسرى، وتنقلب عند ضيق النافذة.
 *
 *   ٣. الموضع يُحسب من مستطيل الزرّ لحظة الفتح، ويُغلَق عند التمرير: قائمة
 *      تبقى معلّقة في مكانها بينما انزلق صفُّها من تحتها تُنفَّذ على السجل
 *      الخطأ.
 */

export interface MenuAction {
  key: string;
  label: string;
  icon?: IconName;
  onSelect: () => void;
  /** يُعرض معطّلًا مع سبب بدل إخفائه — الغياب بلا تفسير يُقرأ عطلًا. */
  disabled?: boolean;
  title?: string;
  /** يفصل ما قبله عمّا بعده. */
  separated?: boolean;
  danger?: boolean;
}

const MENU_WIDTH = 232;
const GAP = 6;

/** ارتفاع العنصر الواحد تقريبًا — يُقدَّر به ارتفاع القائمة قبل رسمها. */
const ITEM_HEIGHT = 38;

/** حشو الإطار: أعلى وأسفل. */
const PANEL_PADDING = 10;

/**
 * أدنى ارتفاع تُفتح به القائمة.
 *
 * أقلّ منه تصير القائمة شريطًا لا يُقرأ. وحين لا يتّسع الطرفان لهذا القدر
 * تُفتح به على أي حال وتُمرَّر داخل نفسها — وهو أفضل من قائمة بارتفاع
 * عنصرين.
 */
const MIN_PANEL = 140;

export function Menu({
  actions,
  label = 'إجراءات',
  className = 'btn btn-sm btn-icon btn-ghost',
  icon = 'more',
  children,
}: {
  actions: MenuAction[];
  label?: string;
  className?: string;
  icon?: IconName;
  /** بديل الأيقونة في الزرّ، حين يكون المرساة بطاقة لا زرًّا. */
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const [active, setActive] = useState(-1);
  const anchor = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const el = anchor.current;
    if (!el) return;
    const r = el.getBoundingClientRect();

    /*
     * الإحداثيات فيزيائية لا منطقية.
     *
     * الصفحة `dir="rtl"`، وفيها `inset-inline-end` يُترجَم إلى `left` لا
     * إلى `right`. فحسابُ مسافةٍ من الحافة اليمنى ووضعُها في خاصية منطقية
     * يُظهر القائمة في الجهة المقابلة تمامًا — وهو ما وقع فعلًا.
     *
     * و`left`/`top` لا تلتبسان: تُقاسان من أعلى يسار النافذة مهما كان
     * الاتجاه، والمحاذاة المقصودة تُكتب صراحةً.
     */
    const width = Math.min(MENU_WIDTH, window.innerWidth - GAP * 2);

    /* المحاذاة إلى يمين الزرّ — الطبيعية في صفحة من اليمين إلى اليسار. */
    let left = r.right - width;

    /* ثم القصّ داخل النافذة: زرٌّ قرب الحافة يدفع القائمة خارجها. */
    left = Math.max(GAP, Math.min(left, window.innerWidth - width - GAP));

    /*
     * الجهة تُختار بالأوسع، والارتفاع يُقصّ على ما يتّسع.
     *
     * الحساب السابق كان يقدّر ارتفاعًا ثابتًا ثم يقلب أعلى إن ضاق أسفل —
     * فإن ضاق الطرفان معًا خرجت القائمة عن الشاشة وصار التمرير في الصفحة
     * كلها لا في القائمة. وهنا تُحدَّد `maxHeight` بما يتّسع فعلًا، فتُمرَّر
     * القائمة داخل نفسها مهما ضاق المكان.
     */
    const spaceBelow = window.innerHeight - r.bottom - GAP * 2;
    const spaceAbove = r.top - GAP * 2;

    const wanted = actions.length * ITEM_HEIGHT + PANEL_PADDING;
    const openDown = spaceBelow >= Math.min(wanted, MIN_PANEL) || spaceBelow >= spaceAbove;

    const room = Math.max(MIN_PANEL, openDown ? spaceBelow : spaceAbove);
    const maxHeight = Math.min(wanted, room);

    const top = openDown ? r.bottom + GAP : Math.max(GAP, r.top - maxHeight - GAP);

    setPos({ top, left, maxHeight });
  }, [actions.length]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;

    const close = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        anchor.current?.focus();
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const usable = actions.map((a, i) => (a.disabled ? -1 : i)).filter((i) => i >= 0);
        if (!usable.length) return;
        setActive((cur) => {
          const at = usable.indexOf(cur);
          const next =
            e.key === 'ArrowDown'
              ? usable[(at + 1) % usable.length]!
              : usable[(at - 1 + usable.length) % usable.length]!;
          return next;
        });
      }
    };

    const onPointer = (e: PointerEvent) => {
      const t = e.target as Node;
      if (panel.current?.contains(t) || anchor.current?.contains(t)) return;
      close();
    };

    /*
     * التمرير يُغلق — إلا التمرير داخل القائمة نفسها.
     *
     * المستمع بـ`capture` يلتقط أحداث التمرير من **أي** عنصر، ومنها لوحة
     * القائمة حين يفيض محتواها. فكانت محاولة تمرير القائمة تُغلقها فورًا،
     * وهو ما بدا عطلًا في التمرير وهو في الإغلاق.
     *
     * وسببُ الإغلاق أصلًا قائم: القائمة مرساة إلى مستطيل زرّها لحظة الفتح،
     * فتمريرُ الصفحة يترك الزرّ يتحرّك والقائمة ثابتة فتُنفَّذ على السجل
     * الخطأ. ولا يسري ذلك على تمرير محتواها.
     */
    const onScroll = (e: Event) => {
      if (panel.current?.contains(e.target as Node)) return;
      close();
    };

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointer, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointer, true);
    };
  }, [open, actions]);

  useEffect(() => {
    if (open) setActive(-1);
  }, [open]);

  function run(a: MenuAction): void {
    if (a.disabled) return;
    setOpen(false);
    a.onSelect();
  }

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className={className}
        aria-label={children ? undefined : label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          // الصفّ قد يكون قابلًا للنقر: فتحُ القائمة ليس فتحًا للسجل.
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        {children ?? <Icon name={icon} size={15} />}
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={panel}
            className="menu-panel"
            role="menu"
            aria-label={label}
            style={{
              top: pos.top,
              left: pos.left,
              width: Math.min(MENU_WIDTH, window.innerWidth - GAP * 2),
              maxHeight: pos.maxHeight,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {actions.map((a, i) => (
              <button
                key={a.key}
                type="button"
                role="menuitem"
                className={`menu-item${a.danger ? ' menu-item-danger' : ''}${
                  a.separated ? ' menu-item-sep' : ''
                }${i === active ? ' menu-item-active' : ''}`}
                disabled={a.disabled}
                title={a.title}
                onMouseEnter={() => setActive(i)}
                onClick={() => run(a)}
              >
                {a.icon && <Icon name={a.icon} size={15} />}
                <span>{a.label}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
